import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X, Play, Pause } from 'lucide-react';
import type { ProjectStandupNote, ArchitectStandupNote } from '../../types';
import { subscribeProjectStandupNotes } from '../../utils/projectStandupService';
import { subscribeArchitectStandupNotes } from '../../utils/architectStandupService';
import { safeFormatRelativeTime } from '../../utils/safeFormatRelativeTime';

export type StandupNoteVariant = 'project' | 'architect';

interface ProjectStandupHistoryPopoverProps {
  open: boolean;
  projectId: string;
  projectLabel?: string;
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  variant?: StandupNoteVariant;
  historyTitle?: string;
}

type StandupHistoryNote = ProjectStandupNote | ArchitectStandupNote;

function StandupAudioPlayer({ url }: { url: string }) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      void el.play();
      setPlaying(true);
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={url}
        preload="none"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        className="pm-standup-history-audio-btn"
        onClick={toggle}
        title={playing ? 'Pause' : 'Play recording'}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
      >
        {playing ? <Pause size={12} /> : <Play size={12} />}
      </button>
    </>
  );
}

export const ProjectStandupHistoryPopover: React.FC<ProjectStandupHistoryPopoverProps> = ({
  open,
  projectId,
  projectLabel,
  onClose,
  variant = 'project',
  historyTitle,
}) => {
  const [notes, setNotes] = useState<StandupHistoryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    setError(null);
    setNotes([]);
    const subscribe =
      variant === 'architect' ? subscribeArchitectStandupNotes : subscribeProjectStandupNotes;
    return subscribe(
      projectId,
      list => {
        setNotes(list);
        setLoading(false);
      },
      err => {
        setError(err.message || 'Could not load standup history.');
        setNotes([]);
        setLoading(false);
      },
    );
  }, [open, projectId, variant]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="pm-standup-history-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="pm-standup-history-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Standup note history"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pm-standup-history-header">
          <div className="pm-standup-history-title-block">
            <History size={16} aria-hidden />
            <div>
              <h3 className="pm-standup-history-title">{historyTitle || 'Standup history'}</h3>
              {projectLabel ? (
                <p className="pm-standup-history-sub">{projectLabel}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="pm-standup-history-close"
            onClick={onClose}
            aria-label="Close history"
          >
            <X size={16} />
          </button>
        </div>
        <div className="pm-standup-history-body">
          {loading ? (
            <p className="pm-standup-history-empty">Loading…</p>
          ) : error ? (
            <p className="pm-standup-history-empty pm-standup-history-empty--error">{error}</p>
          ) : notes.length === 0 ? (
            <p className="pm-standup-history-empty">No standup notes yet.</p>
          ) : (
            <ul className="pm-standup-history-list">
              {notes.map(note => (
                <li key={note.id} className="pm-standup-history-item">
                  <div className="pm-standup-history-item-meta">
                    <span className="pm-standup-history-author">{note.createdByName}</span>
                    {safeFormatRelativeTime(note.createdAt) ? (
                      <span className="pm-standup-history-date">
                        {safeFormatRelativeTime(note.createdAt)}
                      </span>
                    ) : null}
                    {note.audioUrl ? <StandupAudioPlayer url={note.audioUrl} /> : null}
                  </div>
                  {note.text ? (
                    <p className="pm-standup-history-text">
                      <span className="pm-standup-history-quote">{note.text}</span>
                    </p>
                  ) : (
                    <p className="pm-standup-history-text pm-standup-history-text--audio-only">
                      Voice note only
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ProjectStandupHistoryPopover;
